import { SimpleCarousel } from '@eventbrite/collection-carousel';
import { track } from '@eventbrite/datalayer-library';
import { Icon } from '@eventbrite/eds-icon';
import { ArrowRightChunky } from '@eventbrite/eds-iconography';
import { FormattedEvent, ShareUTMOptions } from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import { LoadingSkeleton } from '@eventbrite/loading-skeleton';
import { Button } from '@eventbrite/marmalade';
import React from 'react';
import { connect } from 'react-redux';
import DiscoverVerticalEventCard from '../../../../components/DiscoverEventCard/DiscoverVerticalEventCard';
import {
    COLLECTION_SURFACE_MAPPING,
    NYE_COLLECTION_MAP,
    SEASONAL_HOLIDAY_FEATURED,
} from '../../../../constants/seasonalFeatured';
import { ConnectedProps, DiscoverState } from '../../../../types/index';
import { HOMEPAGE_HOLIDAY_FEATURED } from '../../constants/analytics';
import { useCategoryEventsQuery } from '../../hooks/events/events';
import './HolidayFeaturedContainer.scss';

interface HolidayFeaturedProps extends ReduxProps {
    holiday: string;
}
interface TitleSection {
    city: string;
    holiday: string;
    placeId: string;
}

const utmOptions: ShareUTMOptions = {
    'utm-campaign': 'social',
    'utm-content': 'attendeeshare',
    'utm-medium': 'discovery',
    'utm-term': 'listing',
    'utm-source': 'cp',
};

const TitleDescription = React.memo(
    ({ city, holiday, placeId }: TitleSection) => (
        <div className="title-subtitle-container">
            <h2>{SEASONAL_HOLIDAY_FEATURED[holiday].title}</h2>
            {SEASONAL_HOLIDAY_FEATURED[holiday].subTitle != '' && (
                <h3>{SEASONAL_HOLIDAY_FEATURED[holiday].subTitle}</h3>
            )}
            <div className="view-more-link">
                {SEASONAL_HOLIDAY_FEATURED[holiday].description != '' && (
                    <h4>{SEASONAL_HOLIDAY_FEATURED[holiday].description}</h4>
                )}

                <a
                    href={
                        NYE_COLLECTION_MAP[placeId] != undefined
                            ? NYE_COLLECTION_MAP[placeId]
                            : SEASONAL_HOLIDAY_FEATURED[holiday].viewMoreUrl
                    }
                >
                    {gettext('View more')}
                </a>
            </div>

            <div className="holiday-link">
                {SEASONAL_HOLIDAY_FEATURED[holiday].description != '' && (
                    <h4>{SEASONAL_HOLIDAY_FEATURED[holiday].description}</h4>
                )}
                <a href={SEASONAL_HOLIDAY_FEATURED[holiday].viewMoreUrl}>
                    Explore {city} events
                    <Icon type={<ArrowRightChunky />} />
                </a>
            </div>
        </div>
    ),
);

const ExploreMore = React.memo(({ city, holiday, placeId }: TitleSection) => (
    <div className="eds-align--center explore-more-link">
        <a
            href={
                NYE_COLLECTION_MAP[placeId] != undefined
                    ? NYE_COLLECTION_MAP[placeId]
                    : SEASONAL_HOLIDAY_FEATURED[holiday].viewMoreUrl
            }
        >
            <Button variant="ghost" size="block">
                {gettext('Explore %(city)s events', { city })}{' '}
                <Icon type={<ArrowRightChunky />} />
            </Button>
        </a>
    </div>
));

export const HolidayFeatured = ({
    isAuthenticated,
    isMobile,
    location,
    holiday,
    shareOptions,
    locale,
}: HolidayFeaturedProps) => {
    React.useEffect(() => {
        if (location && location.currentPlace) {
            track({
                eventName: 'HomepageHolidayFeaturedView',
                eventData: {
                    searchLocation: location.currentPlace,
                },
            });
        }
    }, [location]);

    //collection query for holiday featured events
    const collection = useCategoryEventsQuery({
        queryOverload: {
            page_size: 10,
            aggs: {},
            collections_or: COLLECTION_SURFACE_MAPPING['Homepage'],
            include_mature_events: true,
            include_manual_events: true,
            dates: ['current_future'],
            price: 'paid',
        },
        refetchOnMount: false,
        location: location,
        enabled: true,
    });

    //fallback query
    const { data, isLoading } = useCategoryEventsQuery({
        queryOverload: {
            page_size: 10,
            tags: SEASONAL_HOLIDAY_FEATURED[holiday].categoryTag,
            aggs: {},
        },
        refetchOnMount: false,
        location: location,
        enabled: true,
    });
    if (collection.isLoading || isLoading) {
        return (
            <div>
                <LoadingSkeleton height={isMobile ? '446px' : '493px'} />
            </div>
        );
    }

    if (
        (!collection.data || collection.data?.events.length == 0) &&
        (!data || data?.events.length == 0)
    )
        return null;

    const events: FormattedEvent[] =
        collection.data?.events.length != 0
            ? collection.data?.events || []
            : data?.events || [];
    const showNavigation = events.length > 5;

    return (
        <div className="nye-featured-events">
            <div className="holiday-featured-events-container">
                <TitleDescription
                    city={location.currentPlace || ''}
                    holiday={holiday}
                    placeId={location.placeId || ''}
                />
                <div className="eoy_lights"></div>
                <div className="simple-carousel-parent">
                    <SimpleCarousel
                        useSideNavigation={showNavigation}
                        slidesVisible={{ desktop: 5, tablet: 3 }}
                    >
                        {events.map((event) => (
                            <div
                                className="holiday-featured-events-event-card"
                                key={event.id}
                            >
                                <DiscoverVerticalEventCard
                                    event={event}
                                    shareOptions={{
                                        ...shareOptions,
                                        utmOptions,
                                        trackingCategory: 'eventcard',
                                    }}
                                    isAuthenticated={isAuthenticated}
                                    affCode="ehomefeatured"
                                    statsigLocationString={
                                        HOMEPAGE_HOLIDAY_FEATURED
                                    }
                                    locale={locale}
                                    isLazyImage
                                />
                            </div>
                        ))}
                    </SimpleCarousel>
                </div>
                <ExploreMore
                    city={location.currentPlace || ''}
                    holiday={holiday}
                    placeId={location.placeId || ''}
                />
            </div>
        </div>
    );
};

const _mapStateToProps = ({
    app,
    user,
    location,
    shareOptions,
}: DiscoverState) => {
    return {
        isMobile: app.isMobile,
        isAuthenticated: user.isAuthenticated,
        location: location,
        shareOptions: shareOptions,
        locale: app.locale,
    };
};

type ReduxProps = ConnectedProps<typeof _mapStateToProps>;
export default connect(_mapStateToProps)(HolidayFeatured);
